import { Environment, CameraControls, Html, PivotControls } from '@react-three/drei'
import { EffectComposer, ToneMapping, Noise, Bloom, Outline } from '@react-three/postprocessing'
import { BlendFunction, KernelSize, ToneMappingMode, Resolution, Resizer } from "postprocessing"
import { useEffect, useMemo, useRef, useState } from 'react'
import { track } from '@vercel/analytics'
import { easing, geometry } from 'maath'

import useGame from './stores/useGame.jsx'
import * as THREE from'three'
import { useFrame, useThree } from '@react-three/fiber'

import { CubeSat } from './components/cubesat.jsx'
import { HetModule } from './components/hetModule.jsx'
import { MPTModule } from './components/mptModule.jsx'
import { BPTModule } from './components/bptModule.jsx'
import { CGTModule } from './components/cgtModule.jsx'
import { GPUStatsMonitor } from './PerformanceMonitor.jsx'


export default function Experience()
{
    const modelGroup = useRef()
    const state = useThree()

    // Check for stats URL parameter
    const [showGPUStats, setShowGPUStats] = useState(false)
    
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const statsParam = urlParams.get('stats')
        setShowGPUStats(statsParam === 'true')
    }, [])

    // All Use Game States Must Remain or adjust based on new states
    const thruster = useGame((state) => state.thruster)
    const phase = useGame((state) => state.phase)
    const touched = useGame((state) => state.touched)
    const zoom = useGame((state) => state.zoom)

    const {scene} = useThree()

    const orientation = useGame((state) => state.orientation)
    const portrait = useGame((state) => state.portrait)
    const landscape = useGame((state) => state.landscape)

    const het  = useGame((state) => state.het)
    const mpt  = useGame((state) => state.mpt)
    const bpt  = useGame((state) => state.bpt)
    const cgt  = useGame((state) => state.cgt)

    const h_fd = useGame((state) => state.h_fd)
    const h_pr = useGame((state) => state.h_pr)
    const h_ps = useGame((state) => state.h_ps)
    const h_null = useGame((state) => state.h_null)

    // Mobile touch delay to prevent ghost clicks
    const [touchBlocked, setTouchBlocked] = useState(false)
    const blockTouch = () => {
        setTouchBlocked(true)
        setTimeout(() => setTouchBlocked(false), 300) // 300ms delay
    }

    const handleTouchSafe = (callback) => {
        return (e) => {
            if (touchBlocked) {
                e.preventDefault()
                return
            }
            callback()
            blockTouch()
        }
    }

    const s_fd = useGame((state) => state.s_fd)
    const s_pr = useGame((state) => state.s_pr)
    const s_ps = useGame((state) => state.s_ps)

    const c_fm = useGame((state) => state.c_fm)
    const c_fi = useGame((state) => state.c_fi)
    const c_ss = useGame((state) => state.c_ss)

    const c_fp = useGame((state) => state.c_fp)
    const c_pr1 = useGame((state) => state.c_pr1)
    const c_pr2 = useGame((state) => state.c_pr2)
    const c_cp = useGame((state) => state.c_cp)

    const c_fd1 = useGame((state) => state.c_fd1)
    const c_fd2 = useGame((state) => state.c_fd2)
    const c_sf1 = useGame((state) => state.c_sf1)
    const c_sf2 = useGame((state) => state.c_sf2)
    const c_iv1 = useGame((state) => state.c_iv1)
    const c_iv2 = useGame((state) => state.c_iv2)

    const c_to = useGame((state) => state.c_to)
    const c_nz = useGame((state) => state.c_nz)



    const S_FD_POI = useRef(new THREE.Vector3(0,0,0))
    const S_PS_POI = useRef(new THREE.Vector3(0,0,0))
    const S_PR_POI = useRef(new THREE.Vector3(0,0,0))


    const out0 = useRef()
    const out1 = useRef()
    const out2 = useRef()

    const infoPanel = useGame((state) => state.infoPanel)

    const cameraControlsRef = useRef()

    // Camera Positions
    // Landscape

    // HET
    const HET_S_FD_Cam_Pos = new THREE.Vector3(-0.00, -0.1, 0.225)
    const HET_S_FD_Cam_Target = new THREE.Vector3(  -0.05, -0.1, 0.0)

    useEffect(() => {
        if(state.size.width < 500)
        {
            portrait()
        }

        if(state.size.width > state.size.height )
        {
            landscape()
        }
    }, [state.size.width, state.size.height, portrait, landscape])

    useEffect(()=>
    {

        
        if(phase === 'zoomed' && state.size.width > state.size.height)
            {
                if(infoPanel === 'null')
                    {
                        var position = new THREE.Vector3(-0.00, -0.1, 0.225)
                        var target = new THREE.Vector3(  -0.05, -0.1, 0.0)
                        cameraControlsRef.current.setLookAt(...position, ...target, true)
                        cameraControlsRef.current.truckSpeed = 0
                        cameraControlsRef.current.dollySpeed = 0
                        cameraControlsRef.current.polarRotateSpeed = 1
                        cameraControlsRef.current.azimuthRotateSpeed = 1
                        cameraControlsRef.current.minDistance = 0  
                        // cameraControlsRef.current.maxAzimuthAngle = Math.PI * 0.1
                        // cameraControlsRef.current.maxPolarAngle = Math.PI * 0.6
                        // cameraControlsRef.current.minAzimuthAngle = Math.PI * -0.1 
                        // cameraControlsRef.current.minPolarAngle = Math.PI * 0.4  
                    }

                // HET Thruster Camera Positions
                if(thruster === 'HET')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.025, -0.065, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.025, -0.065, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.015, -0.125, 0.125)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.015, -0.125, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.045, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.035, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.08, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.07, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0675, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0675, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.15, 0.105)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.15, 0.0)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0225, -0.185, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // MPT Thruster Camera Positions
                else if(thruster === 'MPT')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0, -0.085, 0.11)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0, -0.075, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, -0.02, 0)) || new THREE.Vector3(-0.025, -0.065, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.04, 0, 0)) || new THREE.Vector3(-0.025, -0.065, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.075, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.065, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.11, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.12, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.175, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.175, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0775, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0775, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.005, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.110, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.110, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.110, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.110, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // BPT Thruster Camera Positions
                else if(thruster === 'BPT')
                {
                    console.log('BPT camera positioning triggered for infoPanel:', infoPanel)
                    
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0, -0.075, 0.09)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.025, -0.075, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.02, -0.02, 0)) || new THREE.Vector3(-0.035, 0.015, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.03, 0, 0)) || new THREE.Vector3(-0.035, -0.005, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.10, -0.15, 0.15)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.10, -0.10, 0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.115, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.085, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.115, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.085, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.085, 0.085)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.02, -0.075, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.025, -0.08, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.015, -0.08, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.02, -0.11, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.02, -0.11, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0, -0.075, 0.09)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.165, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0, -0.075, 0.09)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.02, -0.14, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.045, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.035, 0.00)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.18, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.17, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.18, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.17, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // CGT Thruster Camera Positions
                else if(thruster === 'CGT')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.025, -0.065, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.02, 0, 0)) || new THREE.Vector3(-0.025, -0.065, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.02, 0, 0)) || new THREE.Vector3(-0.015, -0.125, 0.125)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.015, -0.125, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.02, 0, 0)) || new THREE.Vector3(-0.08, -0.175, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.075, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.065, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.08, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.08, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.15, 0.105)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.15, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.1, -0.18, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.055, -0.19, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                } 
            }


        // PHONE
        if(phase === 'zoomed' &&  state.size.width < state.size.height)
            {
                if(infoPanel === 'null')
                    {
                        var position = new THREE.Vector3(0.00, -0.1, 0.4)
                        var target = new THREE.Vector3(  -0.060, -0.1, 0.0)
                        cameraControlsRef.current.setLookAt(...position, ...target, true)
                        cameraControlsRef.current.truckSpeed = 0
                        cameraControlsRef.current.dollySpeed = 0
                        cameraControlsRef.current.polarRotateSpeed = 1
                        cameraControlsRef.current.azimuthRotateSpeed = 1
                        cameraControlsRef.current.minDistance = 0  
                        // cameraControlsRef.current.maxAzimuthAngle = Math.PI * 0.1
                        // cameraControlsRef.current.maxPolarAngle = Math.PI * 0.6
                        // cameraControlsRef.current.minAzimuthAngle = Math.PI * -0.1 
                        // cameraControlsRef.current.minPolarAngle = Math.PI * 0.4
                    }

                // HET Thruster Camera Positions (Phone)
                if(thruster === 'HET')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0.1)) || new THREE.Vector3(0.00, -0.065, 0.2)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.065, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.075, 0, 0.1)) || new THREE.Vector3(0.065, -0.18, 0.25)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.075, 0, 0)) || new THREE.Vector3(0.00, -0.18, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.04, 0, 0.05)) || new THREE.Vector3(-0.07, -0.225, 0.15)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.03, 0, 0)) || new THREE.Vector3(-0.07, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.115, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.0075, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.125, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.065, 0.125)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.055, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.075, -0.10, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.08, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.065, -0.165, 0.175)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.065, -0.165, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.06, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.06, -0.205, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.04, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.04, -0.195, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.04, -0.2, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.04, -0.195, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // MPT Thruster Camera Positions (Phone)
                else if(thruster === 'MPT')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0.1)) || new THREE.Vector3(0.00, -0.10, 0.2)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.10, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.075, -0.01, 0.1)) || new THREE.Vector3(0.050, -0.18, 0.25)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.04, -0.01, 0)) || new THREE.Vector3(-0.015, -0.18, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0.1, 0.1, 0.05)) || new THREE.Vector3(-0.06, -0.25, 0.12)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0.04, 0, 0)) || new THREE.Vector3(-0.06, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.120, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.085, 0.15)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.075, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.065, -0.12, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.135, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.195, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.185, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.069, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.069, -0.205, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.045, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.015, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.045, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.045, -0.1975, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.045, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.045, -0.1975, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // BPT Thruster Camera Positions (Phone)
                else if(thruster === 'BPT')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, 0, 0.1)) || new THREE.Vector3(-0.06, -0.10, 0.2)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, -0.025, 0)) || new THREE.Vector3(-0.06, -0.10, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.025, 0.1)) || new THREE.Vector3(0.00, -0.125, 0.25)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.025, -0.025, 0)) || new THREE.Vector3(-0.065, -0.125, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.015, 0.025)) || new THREE.Vector3(-0.09, -0.225, 0.15)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.015, -0.015, 0)) || new THREE.Vector3(-0.09, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.1, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.115, 0.06)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.103, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.01, -0.095, 0.1)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.085, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.000, -0.08, 0.100)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.000, -0.08, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.11, 0.115)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.11, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.170, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.09, -0.170, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.04, -0.15, 0.115)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.15, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.065, -0.20, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.20, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.065, -0.20, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.075, -0.20, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0575, -0.18, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0575, -0.175, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0575, -0.18, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0575, -0.175, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0385, -0.185, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0385, -0.175, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0385, -0.185, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0385, -0.175, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.175, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.05, -0.165, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
                
                // CGT Thruster Camera Positions (Phone)
                else if(thruster === 'CGT')
                {
                    if(infoPanel === 'S_FD')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0.025)) || new THREE.Vector3(0.00, -0.10, 0.2)
                        var target = scene.getObjectByName(thruster + '_TAR_S_FD')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.10, 0.0)
                    }
                    else if(infoPanel === 'S_PR')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.025, 0.1)) || new THREE.Vector3(0.065, -0.125, 0.25)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PR')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(-0.05, -0.025, 0)) || new THREE.Vector3(0.00, -0.125, 0.0)
                    }
                    else if(infoPanel === 'S_PS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0.05)) || new THREE.Vector3(-0.07, -0.235, 0.15)
                        var target = scene.getObjectByName(thruster + '_TAR_S_PS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, -0.05, 0)) || new THREE.Vector3(-0.07, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FM')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.120, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FM')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.00, -0.1, 0.0)
                    }
                    else if(infoPanel === 'C_FI')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.01, -0.125, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FI')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.00, -0.105, 0.0)
                    }
                    else if(infoPanel === 'C_SS')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.085, 0.125)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SS')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.025, -0.075, 0.00)
                    }
                    else if(infoPanel === 'C_FP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.075, -0.085, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.050, -0.085, 0.0)
                    }
                    else if(infoPanel === 'C_PR1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.065, -0.165, 0.175)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.065, -0.165, 0.0)
                    }
                    else if(infoPanel === 'C_PR2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.06, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_PR2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.06, -0.205, 0.03)
                    }
                    else if(infoPanel === 'C_CP')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.055, -0.19, 0.075)
                        var target = scene.getObjectByName(thruster + '_TAR_C_CP')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(0.035, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_FD2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_FD2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.085, -0.19, 0.0)
                    }
                    else if(infoPanel === 'C_SF1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.040, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.040, -0.1975, 0.03)
                    }
                    else if(infoPanel === 'C_SF2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.040, -0.205, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_SF2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.040, -0.1975, 0.03)
                    }
                    else if(infoPanel === 'C_IV1')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV1')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_IV2')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.21, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_IV2')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.0125, -0.20, 0.03)
                    }
                    else if(infoPanel === 'C_TO')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_TO')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }
                    else if(infoPanel === 'C_NZ')
                    {
                        var position = scene.getObjectByName(thruster + '_POS_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.195, 0.05)
                        var target = scene.getObjectByName(thruster + '_TAR_C_NZ')?.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 0, 0)) || new THREE.Vector3(-0.03, -0.185, 0.03)
                    }

                    cameraControlsRef.current.setLookAt(...position, ...target, true)
                }
            }



        if(phase === 'standard' && state.size.width > state.size.height)
            {
                var position = new THREE.Vector3(-0.9, 0.5, -1.0)
                var target = new THREE.Vector3(0, 0, 0)
                cameraControlsRef.current.setLookAt(...position, ...target, true)
                cameraControlsRef.current.truckSpeed = 0
                cameraControlsRef.current.dollySpeed = 1
                cameraControlsRef.current.polarRotateSpeed = 1
                cameraControlsRef.current.azimuthRotateSpeed = 1
                cameraControlsRef.current.minDistance = 1
                cameraControlsRef.current.maxAzimuthAngle = Infinity
                cameraControlsRef.current.maxPolarAngle = Math.PI
                cameraControlsRef.current.minAzimuthAngle = -Infinity
                cameraControlsRef.current.minPolarAngle = 0
            }

        if(phase === 'standard' && state.size.width < state.size.height)
            {
                var position = new THREE.Vector3(-0.9, 0.75, -1.5)
                var target = new THREE.Vector3(0, 0, 0)
                cameraControlsRef.current.setLookAt(...position, ...target, true)
                cameraControlsRef.current.truckSpeed = 0
                cameraControlsRef.current.dollySpeed = 1
                cameraControlsRef.current.polarRotateSpeed = 1
                cameraControlsRef.current.azimuthRotateSpeed = 1
                cameraControlsRef.current.minDistance = 1
                cameraControlsRef.current.maxAzimuthAngle = Infinity
                cameraControlsRef.current.maxPolarAngle = Math.PI
                cameraControlsRef.current.minAzimuthAngle = -Infinity
                cameraControlsRef.current.minPolarAngle = 0
            }

    },[thruster, phase, state.size.width, infoPanel])

    // Highlighted Objects
    const [ hi, setHi ] = useState('none')
    useMemo(()=>
    {if(state.size.width > 0)
    {
        if(hi === 'none')
            {
                out0.current = undefined
                out1.current = undefined
                out2.current = undefined
            }

        if(hi === 'fdv1')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FD1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
            }
        
        if(hi === 'fdv')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FD1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
            }

        if(hi === 'fdv2')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FD2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
            }
        if(hi === 'pr1')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('S_PS_C_PR1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
            }

        if(hi === 'pr2')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('S_PR_C_PR2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                
            }

        if(hi === 'sf1')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_SF1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
            }
        
        if(hi === 'sf2')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_SF2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
            }

        if(hi === 'iv1')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_IV1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
            }

        if(hi === 'iv2')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_IV2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
            }


        if(hi === 'fi')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FI') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined  
                        out2.current = undefined  

                    }})
            }

        if(hi === 'ssv')
            {
               state.scene.traverse(obj => {
                    if(obj.name.includes('Red') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                    }})

                state.scene.traverse(obj => {
                    if(obj.name.includes('White') && obj.name.includes(thruster)){
                        out1.current = undefined
                        out2.current = obj
                    }})
            }

        if(hi === 'fm')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FM') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined                          
                    }})
            }

        if(hi === 'fpr')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FP') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined   
                    }})
            }

        

        if(hi === 'cpr')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_CP') && obj.name.includes(thruster)){
                        out0.current = obj

                        out2.current = undefined  
                    }
                })
            }
        
        if(hi === 'to')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_TO') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }
                })
            }
        
        if(hi === 'nz')
            {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_NZ') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }
                })
            }
        
    }
    },[hi])

    useMemo(()=>
        {
            if( infoPanel === 'S_PS' || infoPanel === 'S_PR' || infoPanel === 'S_FD' || infoPanel === 'null' ) 
            {
                out0.current = undefined     
                out1.current = undefined  
                out2.current = undefined  

                if(state.size.width > 0)
                {
                    setHi('none')
                }
            }

            if(infoPanel == 'C_FM')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FM') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  

                    }})
                }

            if(infoPanel == 'C_FI')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FI') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined  
                        out2.current = undefined  

                    }})
                }

            if(infoPanel == 'C_SS')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('Red') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                    }})
                state.scene.traverse(obj => {
                    if(obj.name.includes('White') && obj.name.includes(thruster)){
                        out2.current = obj
                    }})
                }

            if(infoPanel == 'C_FP')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FP') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined   
                    }})
                }

            if(infoPanel == 'C_PR1')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_PR1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
                }
            if(infoPanel == 'C_PR2')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_PR2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined
                    }
                })
                }

            if(infoPanel == 'C_CP')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_CP') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }
                })}

            if(infoPanel == 'C_FD1')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FD1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }
            if(infoPanel == 'C_FD2')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_FD2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }
            

            if(infoPanel == 'C_SF1')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_SF1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }
            if(infoPanel == 'C_SF2')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_SF2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }

            if(infoPanel == 'C_TO')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_TO') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }

            if(infoPanel == 'C_NZ')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_NZ') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }

            if(infoPanel == 'C_IV1')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_IV1') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }
            if(infoPanel == 'C_IV2')
                {
                state.scene.traverse(obj => {
                    if(obj.name.includes('C_IV2') && obj.name.includes(thruster)){
                        out0.current = obj
                        out1.current = undefined
                        out2.current = undefined  
                    }})
                }

    },[thruster, infoPanel, phase])

        const Points = () =>
            {
                const POI_S_FD_Position = useRef()
                const POI_S_PS_Position = useRef()
                const POI_S_PR_Position = useRef()

                useFrame((state) =>{
                    if(POI_S_FD_Position.current != undefined)
                    {
                        POI_S_FD_Position.current.position.x = scene.getObjectByName( thruster + '_POI_S_FD').getWorldPosition(new THREE.Vector3()).x
                        POI_S_FD_Position.current.position.y = scene.getObjectByName( thruster + '_POI_S_FD').getWorldPosition(new THREE.Vector3()).y
                        POI_S_FD_Position.current.position.z = scene.getObjectByName( thruster + '_POI_S_FD').getWorldPosition(new THREE.Vector3()).z
                     }

                    if(POI_S_PS_Position.current != undefined)
                    {
                        POI_S_PS_Position.current.position.x = scene.getObjectByName( thruster + '_POI_S_PS').getWorldPosition(new THREE.Vector3()).x
                        POI_S_PS_Position.current.position.y = scene.getObjectByName( thruster + '_POI_S_PS').getWorldPosition(new THREE.Vector3()).y 
                        POI_S_PS_Position.current.position.z = scene.getObjectByName( thruster + '_POI_S_PS').getWorldPosition(new THREE.Vector3()).z
                     }

                     
                    if(POI_S_PR_Position.current != undefined)
                    {
                        POI_S_PR_Position.current.position.x = scene.getObjectByName( thruster + '_POI_S_PR').getWorldPosition(new THREE.Vector3()).x
                        POI_S_PR_Position.current.position.y = scene.getObjectByName( thruster + '_POI_S_PR').getWorldPosition(new THREE.Vector3()).y
                        POI_S_PR_Position.current.position.z = scene.getObjectByName( thruster + '_POI_S_PR').getWorldPosition(new THREE.Vector3()).z
                     }
                })

            return  <>
            {/***  Section POI's ***/}
                { phase === 'zoomed' && infoPanel === 'null' &&<>
                <group ref={POI_S_FD_Position}>
                    <Html > 
                        <div className="textButtonContainer">
                            <div 
                                className="textButton" 
                                onClick={()=> { s_fd(); track(thruster + '_Section_Flow_Divider_Viewed'); } }
                                onTouchStart={()=> { s_fd(); track(thruster + '_Section_Flow_Divider_Viewed'); } }
                                onMouseOver={()=> { h_fd() } }
                                onMouseOut={()=> { h_null() } }
                                onMouseUp={()=> { h_null() } }
                            >
                                {thruster === 'HET' ? 'FLOW DIVIDER' : (['CGT','BPT','MPT'].includes(thruster) ? 'THRUSTER' : 'FLOW DIVIDER')}
                                
                            </div> 

                            <div 
                                className="textButtonArrow" 
                                onClick={()=> { s_fd(); track(thruster + '_Section_Flow_Divider_Viewed'); } }
                                onTouchStart={()=> { s_fd(); track(thruster + '_Section_Flow_Divider_Viewed'); } }
                                onMouseOver={()=> { h_fd() } }
                                onMouseOut={()=> { h_null() } }
                                onMouseUp={()=> { h_null() } }>
                            </div> 
                        </div>
                    </Html>
                </group>

                <group ref={POI_S_PR_Position}>
                    <Html > 
                        <div className="textButtonContainer">
                            <div 
                                className="textButton" 
                                onClick={()=> { s_pr(); track(thruster + '_Section_Pressure_Regulation_Viewed'); }}
                                onTouchStart={()=> { s_pr(); track(thruster + '_Section_Pressure_Regulation_Viewed'); }}
                                onMouseOver={()=> { h_pr() } }
                                onMouseOut={()=> { h_null() } } 
                                onMouseUp={()=> { h_null() } }
                            >
                            {['HET','CGT'].includes(thruster)
                                ? 'PRESSURE REGULATION'
                                : (['MPT','BPT'].includes(thruster)
                                        ? (<span>PRESSURIZATION AND<br/>PRESSURE REGULATION</span>)
                                        : 'PRESSURE REGULATION')}

                            </div> 

                            <div 
                                className="textButtonArrow" 
                                onClick={()=> { s_pr(); track(thruster + '_Section_Pressure_Regulation_Viewed'); } }
                                onTouchStart={()=> { s_pr(); track(thruster + '_Section_Pressure_Regulation_Viewed'); } }
                                onMouseOver={()=> { h_pr() } }
                                onMouseOut={()=> { h_null() } } 
                                onMouseUp={()=> { h_null() } }
                                >
                            </div> 
                        </div>
                    </Html>
                </group>

                <group ref={POI_S_PS_Position}>
                    <Html > 
                        <div className="textButtonContainer">
                            <div 
                                className="textButton" 
                                onClick={()=> { s_ps(); track(thruster + '_Section_Propellant_Storage_Viewed'); } }
                                onTouchStart={()=> { s_ps(); track(thruster + '_Section_Propellant_Storage_Viewed'); } }
                                onMouseOver={()=> { h_ps() } }
                                onMouseOut={()=> { h_null() } }
                                onMouseUp={()=> { h_null() } }

                                >
                                PROPELLANT STORAGE
                            </div> 

                            <div 
                                className="textButtonArrow" 
                                onClick={()=> { s_ps(); track(thruster + '_Section_Propellant_Storage_Viewed'); } }
                                onTouchStart={()=> { s_ps(); track(thruster + '_Section_Propellant_Storage_Viewed'); } }
                                onMouseOver={()=> { h_ps() } }
                                onMouseOut={()=> { h_null() } }
                                onMouseUp={()=> { h_null() } }>
                            </div> 
                        </div>
                    </Html>
                </group>
                    </>
                }

            {/***  Component POI's ***/}

            {/*** S_FD ***/}
                { infoPanel === 'S_FD' && <>
                    {/* HET: FM, FI, SS */}
                    {thruster === 'HET' && <>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_FM').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_fm(); track(thruster + '_Components_Flow_Metering_Viewed'); } }
                                onTouchStart={()=> { c_fm(); track(thruster + '_Components_Flow_Metering_Viewed'); } }
                                onMouseOver={()=> { setHi('fm') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton" >Flow Metering</div> 
                                <div className="textButtonArrow"></div>
                            </div>
                        </Html>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_FI').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_fi(); track(thruster + '_Components_Filters_Viewed'); } }
                                onTouchStart={()=> { c_fi(); track(thruster + '_Components_Filters_Viewed'); } }
                                onMouseOver={()=> { setHi('fi') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Filters</div> 
                                <div className="textButtonArrow"></div>
                            </div>
                        </Html>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_SS').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); } }
                                onTouchStart={()=> { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); } }
                                onMouseOver={()=> { setHi('ssv') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Selector Solenoid Valves</div>
                                <div className="textButtonArrow"></div> 
                            </div>
                        </Html>
                    </>}
                    
                    {/* CGT: NZ, SS */}
                    {thruster === 'CGT' && <>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_NZ').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_nz(); track(thruster + '_Components_Nozzle_Viewed'); } }
                                onTouchStart={handleTouchSafe(() => { c_nz(); track(thruster + '_Components_Nozzle_Viewed'); })}
                                onMouseOver={()=> { setHi('nz') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Nozzles</div>
                                <div className="textButtonArrow"></div> 
                            </div>
                        </Html>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_SS').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); } }
                                onTouchStart={handleTouchSafe(() => { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); })}
                                onMouseOver={()=> { setHi('ssv') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Selector Solenoid Valves</div>
                                <div className="textButtonArrow"></div> 
                            </div>
                        </Html>
                    </>}

                    {/* MPT/BPT: SS, NZ, TO */}
                    {['MPT','BPT'].includes(thruster) && <>
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_SS').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); } }
                                onTouchStart={handleTouchSafe(() => { c_ss(); track(thruster + '_Components_Selector_Solenoid_Valves_Viewed'); })}
                                onMouseOver={()=> { setHi('ssv') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Selector Solenoid Valves</div>
                                <div className="textButtonArrow"></div> 
                            </div>
                        </Html>
                        {thruster !== 'MPT' && (
                            <Html position={ scene.getObjectByName( thruster + '_POI_C_NZ').getWorldPosition(new THREE.Vector3())}> 
                                <div className="textButtonContainer2"
                                    onClick={()=> { c_nz(); track(thruster + '_Components_Nozzle_Viewed'); } }
                                    onTouchStart={handleTouchSafe(() => { c_nz(); track(thruster + '_Components_Nozzle_Viewed'); })}
                                    onMouseOver={()=> { setHi('nz') } }
                                    onMouseOut={()=> { setHi('none') } }
                                >
                                    <div className="textButton">Nozzles</div>
                                    <div className="textButtonArrow"></div> 
                                </div>
                            </Html>
                        )}
                        <Html position={ scene.getObjectByName( thruster + '_POI_C_TO').getWorldPosition(new THREE.Vector3())}> 
                            <div className="textButtonContainer2"
                                onClick={()=> { c_to(); track(thruster + '_Components_Trim_Orifice_Viewed'); } }
                                onTouchStart={handleTouchSafe(() => { c_to(); track(thruster + '_Components_Trim_Orifice_Viewed'); })}
                                onMouseOver={()=> { setHi('to') } }
                                onMouseOut={()=> { setHi('none') } }
                            >
                                <div className="textButton">Trim Orifices</div>
                                <div className="textButtonArrow"></div> 
                            </div>
                        </Html>
                    </>}
                </>}


            {/*** S_PR ***/}    
                {/* S_PR: HET/CGT: CP, FP, PR2; MPT/BPT: CP, FD2, IV2, PR2, SF2 */}
                { infoPanel === 'S_PR' && (["HET","CGT"].includes(thruster)) && <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_CP').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_cp(); track(thruster + '_Components_Coarse_Pressure_Regulation_Viewed');} }
                            onTouchStart={()=> { c_cp(); track(thruster + '_Components_Coarse_Pressure_Regulation_Viewed');} }
                            onMouseOver={()=> { setHi('cpr') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Coarse Pressure Regulation</div>
                            <div className="textButtonArrow"></div> 
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_PR2').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_pr2(); track(thruster + '_Components_PR_Pressure_Relief_Viewed'); } }
                            onTouchStart={()=> { c_pr2(); track(thruster + '_Components_PR_Pressure_Relief_Viewed'); } }
                            onMouseOver={()=> { setHi('pr2') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Pressure Relief</div> 
                            <div className="textButtonArrow"></div>
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_FP').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_fp(); track(thruster + '_Components_Fine_Pressure_Relief_Viewed'); } }
                            onTouchStart={()=> { c_fp(); track(thruster + '_Components_Fine_Pressure_Relief_Viewed'); } }
                            onMouseOver={()=> { setHi('fpr') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton" >Fine Pressure Regulation</div>
                            <div className="textButtonArrow"></div>
                        </div>
                    </Html>
                </>}

                { infoPanel === 'S_PR' && (["MPT","BPT"].includes(thruster)) && <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_CP').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_cp(); track(thruster + '_Components_Coarse_Pressure_Regulation_Viewed');} }
                            onTouchStart={()=> { c_cp(); track(thruster + '_Components_Coarse_Pressure_Regulation_Viewed');} }
                            onMouseOver={()=> { setHi('cpr') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Coarse Pressure Regulation</div>
                            <div className="textButtonArrow"></div> 
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_FD2').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_fd2(); track(thruster + '_Components_Flow_Divider_2_Viewed');} }
                            onTouchStart={()=> { c_fd2(); track(thruster + '_Components_Flow_Divider_2_Viewed');} }
                            onMouseOver={()=> { setHi('fdv2') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">FDV</div>
                            <div className="textButtonArrow"></div> 
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_IV2').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_iv2(); track(thruster + '_Components_Isolation_Valve_2_Viewed');} }
                            onTouchStart={()=> { c_iv2(); track(thruster + '_Components_Isolation_Valve_2_Viewed');} }
                            onMouseOver={()=> { setHi('iv2') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Isolation Valves</div>
                            <div className="textButtonArrow"></div> 
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_PR2').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_pr2(); track(thruster + '_Components_PR_Pressure_Relief_Viewed'); } }
                            onTouchStart={()=> { c_pr2(); track(thruster + '_Components_PR_Pressure_Relief_Viewed'); } }
                            onMouseOver={()=> { setHi('pr2') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Pressure Relief</div> 
                            <div className="textButtonArrow"></div>
                        </div>
                    </Html>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_SF2').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_sf2(); track(thruster + '_Components_System_Filter_2_Viewed');} }
                            onTouchStart={()=> { c_sf2(); track(thruster + '_Components_System_Filter_2_Viewed');} }
                            onMouseOver={()=> { setHi('sf2') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">System Filters</div>
                            <div className="textButtonArrow"></div> 
                        </div>
                    </Html>
                </>}


            {/*** S_PS ***/}
                { infoPanel === 'S_PS' &&  <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_FD1').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2" 
                            onClick={()=> { c_fd1(); track(thruster + '_Components_Fill_Drain_Valve_Viewed'); } }
                            onTouchStart={()=> { c_fd1(); track(thruster + '_Components_Fill_Drain_Valve_Viewed'); } }
                            onMouseOver={()=> { setHi('fdv') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton" >FDV</div>
                            <div className="textButtonArrow">
                        </div>   
                        </div>
                    </Html>
                </>}

                { infoPanel === 'S_PS' &&  <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_PR1').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_pr1(); track(thruster + '_Components_PS_Pressure_Relief_Viewed'); } }
                            onTouchStart={()=> { c_pr1(); track(thruster + '_Components_PS_Pressure_Relief_Viewed'); } }
                            onMouseOver={()=> { setHi('pr1') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton">Pressure Relief</div> 
                            <div className="textButtonArrow"></div>
                        </div>
                    </Html>
                </>}

                { infoPanel === 'S_PS'  &&  <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_SF1').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_sf1(); track(thruster + '_Components_System_Filter_Viewed'); } }
                            onTouchStart={()=> { c_sf1(); track(thruster + '_Components_System_Filter_Viewed'); } }
                            onMouseOver={()=> { setHi('sf1') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton" >System Filters</div>
                            <div className="textButtonArrow"></div>
 
                        </div>
                    </Html>
                </>}

                { infoPanel === 'S_PS'  &&  <>
                    <Html position={ scene.getObjectByName( thruster + '_POI_C_IV1').getWorldPosition(new THREE.Vector3())}> 
                        <div className="textButtonContainer2"
                            onClick={()=> { c_iv1(); track(thruster + '_Components_Isolation_Valve_Viewed'); } }
                            onTouchStart={()=> { c_iv1(); track(thruster + '_Components_Isolation_Valve_Viewed'); } }
                            onMouseOver={()=> { setHi('iv1') } }
                            onMouseOut={()=> { setHi('none') } }
                        >
                            <div className="textButton" >Isolation Valves</div> 
                            <div className="textButtonArrow"></div>

                        </div>
                    </Html>
                </>}
                </>
            }

     return <>
           
            <directionalLight 
                color={new THREE.Color( 'white' )}
                position={[-1.4, 5, 3]} 
                intensity={ 3 } 
                castShadow 
                shadow-mapSize={[4096, 4096]}
                shadow-bias={[-0.000005]}
                shadow-camera-top={1}
                shadow-camera-bottom={-1}
                shadow-camera-left={-1}
                shadow-camera-right={1}
            />

            <CameraControls 
                ref={cameraControlsRef}
                minDistance={1}
                maxDistance={2}
                smoothTime={1}
                truckSpeed={0}
                makeDefault
                onStart={touched}
            />

            <Points/>

            <Environment 
                files={['earth.webp','earth-gainmap.webp', 'earth.json']}
                background={true} 
                backgroundIntensity={1.25}
                environmentIntensity={1.0}    
                backgroundRotation={[Math.PI*2.0, Math.PI*-0.9, Math.PI*1.6]}
                environmentRotation={[Math.PI*2.0, Math.PI*-0.9, Math.PI*1.6]}  
                flipX ={true}                     
            />
            
            <group ref={modelGroup} rotation={[Math.PI*-0.50,Math.PI*0.40, Math.PI*0.5]} position={[0,-0.1,0]}>

                <CubeSat/>   
                <HetModule/>
                <MPTModule/>
                <BPTModule/>
                <CGTModule/>

            </group>
            <EffectComposer stencilBuffer disableNormalPass autoClear={false}>
                
                {out0.current != undefined && 
                    <Outline 
                        selection={[out0]} 
                        visibleEdgeColor="#ea0029" 
                        hiddenEdgeColor="#400f0e" 
                        edgeStrength={5}
                        selectionLayer={10}
                        blur={false} // whether the outline should be blurred
                        xRay={true} // indicates whether X-Ray outlines are enabled
                        blendFunction={BlendFunction.ALPHA}
                        width={Resizer.AUTO_SIZE} // render width
                        height={Resizer.AUTO_SIZE} // render height
                        kernelSize={KernelSize.VERY_SMALL} // blur kernel size // set this to BlendFunction.ALPHA for dark outlines
                />}

                {out1.current != undefined && 
                    <Outline 
                        selection={[out1]} 
                        visibleEdgeColor="#ea0029" 
                        hiddenEdgeColor="#400f0e" 
                        edgeStrength={5}
                        selectionLayer={11}
                        blur={false} // whether the outline should be blurred
                        xRay={true} // indicates whether X-Ray outlines are enabled
                        blendFunction={BlendFunction.ALPHA}
                        width={Resizer.AUTO_SIZE} // render width
                        height={Resizer.AUTO_SIZE} // render height
                        kernelSize={KernelSize.VERY_SMALL} // blur kernel size // set this to BlendFunction.ALPHA for dark outlines
                />}

                {out2.current != undefined && 
                    <Outline 
                        selection={[out2]} 
                        visibleEdgeColor="white" 
                        hiddenEdgeColor="black" 
                        edgeStrength={5}
                        selectionLayer={12}
                        blur={false} // whether the outline should be blurred
                        xRay={true} // indicates whether X-Ray outlines are enabled
                        blendFunction={BlendFunction.ALPHA}
                        width={Resizer.AUTO_SIZE} // render width
                        height={Resizer.AUTO_SIZE} // render height
                        kernelSize={KernelSize.VERY_SMALL} // blur kernel size // set this to BlendFunction.ALPHA for dark outlines
                />}

                <Noise 
                    blendFunction={BlendFunction.MULTIPLY}
                    opacity={0.5}
                />

                <ToneMapping mode={ ToneMappingMode.REINHARD }/>

                <Bloom
                    blendFunction={BlendFunction.SCREEN}
                    radius={0.9}                    
                    kernelSize={KernelSize.VERY_LARGE}
                    luminanceThreshold= {0.95} 
                    luminanceSmoothing={0.025}
                    intensity= {0.25} 
                    levels= {6}
                    mipmapBlur={true} // Enables or disables mipmap blur.
                    resolutionX={Resolution.AUTO_SIZE} // The horizontal resolution.
                    resolutionY={Resolution.AUTO_SIZE} // The vertical resolution.
                    />

            </EffectComposer>
        
            {/* GPU Stats Monitor - only show when stats=true in URL */}
            {showGPUStats && <GPUStatsMonitor enabled={true} />}
        
        </>

    
}
